import {
  APPLICATION_ACCESS_CONTROL_FULL_REFERENCE,
  APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE,
} from '@zaoblako/shared/modules/security/constants/application-access-control.constants';
import { AccessLevelEnum } from '@zaoblako/shared/modules/security/enums/access.level.enum';
import type { ApplicationAccessControlFullFindInterface } from '@zaoblako/shared/modules/security/dtos/application/access-control/application-access-control-full-find.dto';
import type { ApplicationAccessControlRatePlanFormInterface } from '@zaoblako/shared/modules/security/dtos/application/access-control/application-access-control-rate-plan-form.dto';
import type { ApplicationAccessControlRoleFormInterface } from '@zaoblako/shared/modules/security/dtos/application/access-control/application-access-control-role-form.dto';

import { cloneDeep, max, compact, merge } from 'lodash';
import { Query } from 'mingo';

/** {@link ApplicationAccessControlUtilsClass.mergeAndCompute} method parameters */
export interface MergeAndComputeParams {
  /** Rate plan access control settings ({@link APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE} by default) */
  fromRatePlan?: ApplicationAccessControlRatePlanFormInterface;
  /** Role access control settings */
  fromRoles?: ApplicationAccessControlRoleFormInterface | ApplicationAccessControlRoleFormInterface[];
}

/** Provides common utils for application access control object */
export class ApplicationAccessControlUtilsClass {
  private static instance: ApplicationAccessControlUtilsClass = null;

  public static getInstance(): ApplicationAccessControlUtilsClass {
    if (!ApplicationAccessControlUtilsClass.instance) {
      ApplicationAccessControlUtilsClass.instance = new ApplicationAccessControlUtilsClass();
    }
    return ApplicationAccessControlUtilsClass.instance;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * Merge multiple access control settings objects into one and compute enabled modules
   *
   * @param params Method parameters
   */
  public mergeAndCompute(params: MergeAndComputeParams): ApplicationAccessControlFullFindInterface {
    const fromRatePlan = params.fromRatePlan || APPLICATION_ACCESS_CONTROL_RATE_PLAN_REFERENCE;
    const fromRoles = compact(
      Array.isArray(params.fromRoles)
        ? params.fromRoles
        : [params.fromRoles],
    );

    const base = cloneDeep(APPLICATION_ACCESS_CONTROL_FULL_REFERENCE);

    // Compute module access control without module enabled flag
    for (const [moduleName, module] of Object.entries(base)) {
      // Compute sections access control
      for (const [sectionName, section] of Object.entries(module.sections)) {
        // Take highest access level for tabs
        for (const [tabName, tab] of Object.entries(section.tabs)) {
          tab.enabled = tab.editable
            ? max([
              fromRatePlan?.[moduleName]?.sections?.[sectionName]?.tabs?.[tabName]?.enabled ?? false,
              base?.[moduleName]?.sections?.[sectionName]?.tabs?.[tabName]?.enabled ?? false,
            ])
            : true;
          tab.accessLevel = max([
            ...fromRoles.map(item => item?.[moduleName]?.sections?.[sectionName]?.tabs?.[tabName]?.accessLevel ?? AccessLevelEnum.FORBIDDEN),
            tab.accessRange[0],
          ]);
          tab.permissions = merge(tab.permissions, ...fromRoles.map(item => item?.[moduleName]?.sections?.[sectionName]?.tabs?.[tabName]?.permissions ?? {}));
        }

        // Take highest allow flag for permissions
        for (const [permissionName, permission] of Object.entries(section.permissions)) {
          permission.isAllowed = max([
            ...fromRoles.map(item => item?.[moduleName]?.sections?.[sectionName]?.permissions?.[permissionName]?.isAllowed ?? false),
            permission.isAllowed,
          ]);
        }
      }

      // Compute module enable flag
      module.enabled = max([
        fromRatePlan?.[moduleName]?.enabled ?? false,
        base?.[moduleName]?.enabled ?? false,
      ]);
    }

    // Re-compute module enable flag using dependencies
    for (const module of Object.values(base)) {
      if (!module.dependencies?.length) {
        continue;
      }

      module.enabled = module.enabled && module.dependencies.every(dependency => base[dependency]?.enabled);
    }

    return base;
  }

  /**
   * Bulk permissions grant for access control object
   *
   * @param accessControl Target access control object
   * @param accessLevel Minimum access level to grant
   * @param isAllowed Minimum permission level to grant
   *
   * @returns Same access control object (for chaining)
   */
  public bulkGrant(
    accessControl: ApplicationAccessControlRoleFormInterface,
    accessLevel: AccessLevelEnum,
    isAllowed: boolean,
  ): ApplicationAccessControlRoleFormInterface {
    for (const module of Object.values(accessControl)) {
      for (const section of Object.values(module.sections)) {
        for (const tab of Object.values(section.tabs)) {
          tab.accessLevel = max([tab.accessLevel, accessLevel]);
        }

        for (const permission of Object.values(section.permissions)) {
          permission.isAllowed = max([permission.isAllowed, isAllowed]);
        }
      }
    }
    return accessControl;
  }

  /**
   * Test access control settings
   *
   * @param accessControl Access control settings to test
   * @param expressions MongoDB-like query object to match access control settings
   *
   * @returns Check result
   *
   * @example
   * ApplicationAccessControlUtilsClass.getInstance().test(this.context?.auth?.accessControl, {
   *   'BASIS.enabled': true,
   *   'BASIS.sections.SECURITY.tabs.USER.accessLevel': { $gte: AccessLevelEnum.WRITE },
   *   'BASIS.sections.SECURITY.permissions.CAN_BAN_USER.isAllowed': true,
   * })
   */
  public test(accessControl: ApplicationAccessControlFullFindInterface, expressions: Record<string, any>): boolean {
    const query = new Query(expressions);
    return query.test(accessControl);
  }
}
